/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { IMediaPersonModel, useRTL } from "@bms/common";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import "./MediaPeople.scss";

export interface IMediaPeopleProps {
  people?: IMediaPersonModel[];
  loading?: boolean;
}

export const MediaPeople: React.FC<IMediaPeopleProps> = ({
  people,
  loading,
}) => {
  const { t } = useTranslation();
  const isRTL = useRTL();

  const roles = useMemo(() => {
    const rolesMap = new Map<
      string,
      {
        PersonRoleCode: string;

        PersonRoleDisplayName: string;

        PersonRoleTranslationKey: string;
      }
    >();

    if (people && people.length > 0) {
      people.forEach((person) => {
        if (!rolesMap.has(person.PersonRoleCode)) {
          rolesMap.set(person.PersonRoleCode, {
            PersonRoleCode: person.PersonRoleCode,
            PersonRoleDisplayName: person.PersonRoleDisplayName,
            PersonRoleTranslationKey: person.PersonRoleTranslationKey,
          });
        }
      });
    }

    return Array.from(rolesMap.values());
  }, [people]);

  const renderPeople = () => {
    if (!people || people?.length === 0) {
      return null;
    }

    return roles.map((role) => {
      const peopleInRole = people
        .filter((row) => row.PersonRoleCode === role.PersonRoleCode)
        .map((row) => row.PersonFullName);

      const peopleInRoleInfo = isRTL
        ? peopleInRole.reverse().join(", ")
        : peopleInRole.join(", ");

      return (
        <div key={role.PersonRoleCode} className="MediaPeople__director">
          <span>
            {t(role.PersonRoleTranslationKey, role.PersonRoleDisplayName)}
            :&nbsp;
          </span>
          <span>{peopleInRoleInfo}</span>
        </div>
      );
    });
  };

  if (!people && loading) {
    return (
      <>
        <div className="MediaPeople__skeleton" style={{ width: "12rem" }} />
        <div className="MediaPeople__skeleton" style={{ width: "9.5rem" }} />
        <div className="MediaPeople__skeleton" style={{ width: "14.5rem" }} />
      </>
    );
  }

  if (!roles || roles.length === 0) {
    return null;
  }

  return <div className="MediaPeople">{renderPeople()}</div>;
};
